import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { localStorageHelper } from '../../../../../utils/localStorageHelper'

const STORAGE_DEFAULTS = {
  quickDate: null,
  customDateRange: null,
  rows: null
}

export default function useActivitySummaryState (mergedConfig) {
  const key = mergedConfig.localStorageKey
  const configQuickDate = mergedConfig.dateRanges?.default?.quickDate ?? 'YTD'
  const configRows = mergedConfig.rows

  const [userState, setUserState] = useState(() => {
    const defaults = { ...STORAGE_DEFAULTS, quickDate: configQuickDate }
    if (!key) return defaults
    return localStorageHelper.loadOrDefault(key, defaults)
  })

  const initializedRef = useRef(false)
  useEffect(() => {
    if (!key) return
    if (!initializedRef.current) {
      initializedRef.current = true
      return
    }
    localStorageHelper.tryStore(key, userState)
  }, [key, userState])

  const quickDate = userState.quickDate ?? configQuickDate
  const customDateRange = userState.customDateRange
  const userRows = userState.rows

  const onQuickDateChange = useCallback((value) => {
    setUserState(prev => {
      const next = { ...prev, quickDate: value }
      if (value !== 'Custom') next.customDateRange = null
      return next
    })
  }, [])

  const onCustomDateRangeChange = useCallback((range) => {
    setUserState(prev => ({ ...prev, customDateRange: range }))
  }, [])

  const onRowsChange = useCallback((rows) => {
    setUserState(prev => ({ ...prev, rows }))
  }, [])

  const onRestoreDefaults = useCallback(() => {
    setUserState({ ...STORAGE_DEFAULTS, quickDate: configQuickDate })
  }, [configQuickDate])

  useEffect(() => {
    if (!key) return
    const stored = localStorageHelper.tryLoad(key)
    if (stored) {
      setUserState(prev => ({ ...prev, ...stored }))
    }
  }, [key])

  const effectiveRows = useMemo(() => {
    if (!userRows || !Array.isArray(configRows)) return configRows
    const configMap = new Map(configRows.map(r => [r.key, r]))
    const seen = new Set()
    const result = []

    for (const ur of userRows) {
      const base = configMap.get(ur.key)
      if (!base) continue
      seen.add(ur.key)
      const merged = { ...base, enabled: ur.enabled }
      if (ur.activityTypes !== undefined) merged.activityTypes = ur.activityTypes
      if (ur.includedActivityKeys !== undefined) merged.includedActivityKeys = ur.includedActivityKeys
      result.push(merged)
    }

    for (const cr of configRows) {
      if (!seen.has(cr.key)) result.push(cr)
    }

    return result
  }, [userRows, configRows])

  const effectiveConfig = useMemo(() => {
    const base = effectiveRows
      ? { ...mergedConfig, rows: effectiveRows }
      : mergedConfig

    if (quickDate === 'Custom' && customDateRange) {
      return {
        ...base,
        dateRanges: {
          ...base.dateRanges,
          default: {
            quickDate: 'Custom',
            startDate: customDateRange.startDate,
            endDate: customDateRange.endDate
          }
        }
      }
    }

    return {
      ...base,
      dateRanges: {
        ...base.dateRanges,
        default: { ...base.dateRanges?.default, quickDate }
      }
    }
  }, [mergedConfig, effectiveRows, quickDate, customDateRange])

  return {
    effectiveConfig,
    quickDate,
    customDateRange,
    effectiveRows: effectiveRows ?? configRows ?? [],
    onQuickDateChange,
    onCustomDateRangeChange,
    onRowsChange,
    onRestoreDefaults
  }
}
